import React, { useMemo, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  IonItem,
  IonLabel,
  IonNote,
  IonSpinner,
  IonToggle,
} from "@ionic/react";
import { cloudDownloadOutline, refreshOutline } from "ionicons/icons";
import {
  collection,
  collectionGroup,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ASSOCIATESTATUS_TYPES, FB_COLLECTION_PATH, WORKFLOW_WERKER_TYPES } from "../functions/postActionTypes";
import { db } from "../firebase";
import { USERS_REPORT_COLUMNS, UsersReportRow } from "../types/usersReport";
import { buildCsvFromRows, downloadCsv } from "../utils/csvExport";
import "./UsersReport.css";

interface BaseUserRecord {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  occupation: string;
  profilePicURL: string;
  isDeleted: boolean;
  userBio: string;
}

const APP_VERSION_KEYS = ["appVersion", "AppVersion", "version", "app_version"];

const formatTimestampValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString();
  }

  if (typeof value === "string") {
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber) && value.trim() !== "") {
      const date = new Date(asNumber);
      return Number.isNaN(date.getTime()) ? "" : date.toISOString();
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? value : new Date(parsed).toISOString();
  }

  if (typeof value === "object") {
    const timestampLike = value as {
      toDate?: () => Date;
      toMillis?: () => number;
      seconds?: number;
    };

    if (typeof timestampLike.toDate === "function") {
      const date = timestampLike.toDate();
      return Number.isNaN(date.getTime()) ? "" : date.toISOString();
    }

    if (typeof timestampLike.toMillis === "function") {
      const date = new Date(timestampLike.toMillis());
      return Number.isNaN(date.getTime()) ? "" : date.toISOString();
    }

    if (typeof timestampLike.seconds === "number") {
      const date = new Date(timestampLike.seconds * 1000);
      return Number.isNaN(date.getTime()) ? "" : date.toISOString();
    }
  }

  return "";
};

const getUserDisplayName = (user?: BaseUserRecord): string => {
  if (!user) {
    return "";
  }

  const combined = `${user.firstName} ${user.lastName}`.trim();
  if (combined) {
    return combined;
  }

  return user.email || user.uid;
};

const getAppVersionFromSettings = (settingsDoc: Record<string, unknown>): string => {
  for (const key of APP_VERSION_KEYS) {
    const value = settingsDoc[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
  }

  return "";
};

const UsersReportTable: React.FC<{ rows: UsersReportRow[] }> = ({ rows }) => {
  if (!rows.length) {
    return (
      <IonNote color="medium" style={{ display: "block", marginTop: 8 }}>
        No rows loaded yet.
      </IonNote>
    );
  }

  return (
    <div style={{ overflowX: "auto", marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1300 }}>
        <thead>
          <tr>
            {USERS_REPORT_COLUMNS.map((column) => (
              <th
                key={String(column.key)}
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid var(--ion-color-step-200)",
                  padding: "8px",
                  fontSize: "0.8rem",
                  whiteSpace: "nowrap",
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.uid}>
              {USERS_REPORT_COLUMNS.map((column) => (
                <td
                  key={`${row.uid}-${String(column.key)}`}
                  style={{
                    borderBottom: "1px solid var(--ion-color-step-100)",
                    padding: "8px",
                    fontSize: "0.8rem",
                    verticalAlign: "top",
                    maxWidth: 240,
                    overflowWrap: "anywhere",
                  }}
                >
                  {String(row[column.key] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const UsersReport: React.FC = () => {
  const [rows, setRows] = useState<UsersReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(true);
  const [progressDone, setProgressDone] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [generatedAt, setGeneratedAt] = useState<string>("");
  const [error, setError] = useState<string>("");

  const progressPercent = useMemo(() => {
    if (!progressTotal) {
      return 0;
    }

    return Math.round((progressDone / progressTotal) * 100);
  }, [progressDone, progressTotal]);

  const buildSingleRow = async (
    user: BaseUserRecord,
    nameByUid: Map<string, string>
  ): Promise<UsersReportRow> => {
    const [presenceSnap, settingsSnap, associationsSnap, chatsSnap, workerJobsSnap, invitedCountSnap] = await Promise.all([
      getDoc(doc(db, FB_COLLECTION_PATH.Users, user.uid, "presence", user.uid)),
      getDoc(doc(db, "userSettings", user.uid)),
      getDoc(doc(db, FB_COLLECTION_PATH.UserAssociations, user.uid)),
      getDoc(doc(db, FB_COLLECTION_PATH.Chats, user.uid)),
      getDocs(
        query(
          collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
          where("docStamp.userInfoTo.uid", "==", user.uid)
        )
      ),
      getCountFromServer(collection(db, FB_COLLECTION_PATH.Users, user.uid, FB_COLLECTION_PATH.JobFeed)),
    ]);

    const lastLoginAt = formatTimestampValue(presenceSnap.data()?.lastActivity);
    const appVersion = settingsSnap.exists() ? getAppVersionFromSettings(settingsSnap.data() as Record<string, unknown>) : "";

    const associateNames: string[] = [];
    if (associationsSnap.exists()) {
      const associationData = associationsSnap.data() as Record<string, unknown>;
      for (const [associateUid, associationEntry] of Object.entries(associationData)) {
        if (!associationEntry || typeof associationEntry !== "object") {
          continue;
        }

        const status = (associationEntry as Record<string, unknown>).associateStatus;
        if (status === ASSOCIATESTATUS_TYPES.Associate) {
          associateNames.push(nameByUid.get(associateUid) || associateUid);
        }
      }
    }
    associateNames.sort((a, b) => a.localeCompare(b));

    const schedulerUidSet = new Set<string>();
    let jobsWorkedCount = 0;

    workerJobsSnap.forEach((jobDoc) => {
      const data = jobDoc.data() as any;
      const schedulerUid = String(data?.docStamp?.userInfoFrom?.uid || "");
      const workerStatus = String(data?.jobInfo?.status?.jobStatusWerker || "");

      if (schedulerUid && schedulerUid !== user.uid) {
        schedulerUidSet.add(schedulerUid);
      }

      if (workerStatus === WORKFLOW_WERKER_TYPES.Past) {
        jobsWorkedCount += 1;
      }
    });

    const schedulerNames = Array.from(schedulerUidSet)
      .map((schedulerUid) => nameByUid.get(schedulerUid) || schedulerUid)
      .sort((a, b) => a.localeCompare(b));

    let dmThreadsCount = 0;
    if (chatsSnap.exists()) {
      const chatData = chatsSnap.data() as Record<string, unknown>;
      for (const chatMeta of Object.values(chatData)) {
        if (!chatMeta || typeof chatMeta !== "object") {
          continue;
        }

        const dmUid = String((chatMeta as Record<string, unknown>).DMuid || "").trim();
        if (dmUid) {
          dmThreadsCount += 1;
        }
      }
    }

    return {
      uid: user.uid,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      company: user.company,
      occupation: user.occupation,
      profilePicURL: user.profilePicURL,
      isDeleted: user.isDeleted,
      userBio: user.userBio,
      lastLoginAt,
      appVersion,
      associateNames: associateNames.join(" | "),
      associatesCount: associateNames.length,
      schedulerNames: schedulerNames.join(" | "),
      schedulersCount: schedulerNames.length,
      dmThreadsCount,
      jobsWorkedCount,
      jobsInvitedTotalCount: invitedCountSnap.data().count,
    };
  };

  const handleGenerateReport = async () => {
    setError("");
    setLoading(true);
    setRows([]);

    try {
      const usersSnapshot = await getDocs(collection(db, FB_COLLECTION_PATH.Users));
      const allUsers = usersSnapshot.docs.map((snapshotDoc) => {
        const userData = snapshotDoc.data() as Record<string, unknown>;
        return {
          uid: String(userData.uid || snapshotDoc.id),
          firstName: String(userData.firstName || ""),
          lastName: String(userData.lastName || ""),
          email: String(userData.email || ""),
          company: String(userData.company || ""),
          occupation: String(userData.occupation || ""),
          profilePicURL: String(userData.profilePicURL || ""),
          isDeleted: Boolean(userData.isDeleted),
          userBio: String(userData.userBio || ""),
        } as BaseUserRecord;
      });

      const filteredUsers = includeDeleted
        ? allUsers
        : allUsers.filter((user) => !user.isDeleted);

      const nameByUid = new Map<string, string>();
      allUsers.forEach((user) => {
        nameByUid.set(user.uid, getUserDisplayName(user));
      });

      setProgressTotal(filteredUsers.length);
      setProgressDone(0);

      const batchSize = 10;
      const nextRows: UsersReportRow[] = [];

      for (let index = 0; index < filteredUsers.length; index += batchSize) {
        const batch = filteredUsers.slice(index, index + batchSize);
        const builtRows = await Promise.all(
          batch.map((user) => buildSingleRow(user, nameByUid))
        );

        nextRows.push(...builtRows);
        setProgressDone(Math.min(filteredUsers.length, index + batch.length));
      }

      nextRows.sort((a, b) => {
        const leftName = `${a.firstName} ${a.lastName}`.trim();
        const rightName = `${b.firstName} ${b.lastName}`.trim();
        return leftName.localeCompare(rightName);
      });

      setRows(nextRows);
      setGeneratedAt(new Date().toISOString());
    } catch (reportError) {
      console.error("Failed to generate users report:", reportError);
      setError((reportError as Error)?.message || "Unknown report generation error");
    }

    setLoading(false);
  };

  const handleExportCsv = () => {
    if (!rows.length) {
      return;
    }

    const csvText = buildCsvFromRows(rows as Record<string, unknown>[], USERS_REPORT_COLUMNS as any);
    const dateSuffix = new Date().toISOString().slice(0, 10);
    downloadCsv(`users-report-${dateSuffix}.csv`, csvText);
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Users Report</IonCardTitle>
        <IonCardSubtitle>
          One row per user in the users collection with behavior metrics.
        </IonCardSubtitle>
      </IonCardHeader>
      <IonCardContent>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <IonLabel style={{ margin: 0 }}>Include deleted users</IonLabel>
          <IonToggle
            checked={includeDeleted}
            onIonChange={(event) => setIncludeDeleted(event.detail.checked)}
            disabled={loading}
          />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          <IonButton
            onClick={handleGenerateReport}
            disabled={loading}
            className="users-report-generate-btn"
          >
            <IonIcon icon={refreshOutline} slot="start" />
            Generate Preview
          </IonButton>
          <IonButton onClick={handleExportCsv} disabled={loading || rows.length === 0} fill="outline">
            <IonIcon icon={cloudDownloadOutline} slot="start" />
            Export CSV
          </IonButton>

          {!!rows.length && (
            <IonBadge color="medium" style={{ alignSelf: "center" }}>
              {rows.length} rows
            </IonBadge>
          )}
        </div>

        {loading && (
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <IonSpinner name="crescent" color="primary" />
            <IonNote color="medium">
              Building report: {progressDone}/{progressTotal} users ({progressPercent}%)
            </IonNote>
          </div>
        )}

        {!!generatedAt && !loading && (
          <IonNote color="medium" style={{ display: "block", marginTop: 12 }}>
            Last generated: {new Date(generatedAt).toLocaleString()}
          </IonNote>
        )}

        {!!error && (
          <IonNote color="danger" style={{ display: "block", marginTop: 12 }}>
            {error}
          </IonNote>
        )}

        <UsersReportTable rows={rows} />
      </IonCardContent>
    </IonCard>
  );
};
